/**
 * Controller - Adds lifecycle management and DOM container to framework classes
 *
 * Third level in inheritance chain (Ancestor → Class → Controller). Introduces the
 * concept of a DOM container element and provides lifecycle hooks for initialization
 * and cleanup. Foundation for all UI components.
 *
 * ARCHITECTURE: Controller bridges framework classes and DOM-based components.
 * Adds:
 * - this.container (DOM element)
 * - this.instances (child component storage)
 * - Lifecycle hooks (prepare, constructor, ready, destructor)
 * - DOM convenience methods (scoped to container)
 * - Event handling (with automatic cleanup)
 *
 * LIFECYCLE SEQUENCE:
 * 1. prepare(setup) - Set up data structures, container determined
 * 2. constructor(setup) - Main initialization, DOM ready
 * 3. ready(setup) - Deferred setup after construction, event listeners attached
 * 4. destructor() - Cleanup when instance destroyed
 *
 * MEMORY: DOM singleton automatically tracks and removes event listeners when
 * Controller instances are destroyed.
 *
 * @class Controller
 * @augments Class
 *
 * @param {Object} setup - Configuration object
 * @param {Element} [setup.container] - DOM element for this instance (created if not provided)
 * @param {boolean} [setup.loadTemplates=false] - Load Handlebars templates during construction
 * @param {boolean} [setup.loadStylesheet=false] - Load CSS stylesheet during construction
 * @param {boolean} [setup.interceptEvents=false] - Restrict event handlers to this container
 * @param {boolean} [setup.hide=false] - Start with container hidden
 * @param {string} [setup.theme] - Theme attribute for container
 * @param {Array} [setup.listeners] - Event listeners to attach in ready() hook
 *
 * @example
 * // Create Controller-based component
 * const MyController = Controller.inherit({
 *     namespace: 'MyController',
 *     prepare(setup) {
 *         // Container available, but not yet styled
 *         this.assign(this.data, {
 *             items: []
 *         });
 *     },
 *     constructor(setup) {
 *         // DOM ready, can query and manipulate
 *         this.on('click', '.button', this.handleClick);
 *     },
 *     ready(setup) {
 *         // Construction complete, can access children
 *     },
 *     destructor() {
 *         // Cleanup (event listeners auto-removed by DOM singleton)
 *     }
 * });
 */
define("Controller", [`Class`, `module`], function (Super, Self) {
	return Super.inherit({
		namespace: Self.id,
		requires: [`DOM`, `System`, `Templates`],
		defaults: {
			loadTemplates: false,
			loadStylesheet: false,
			interceptEvents: false,
		},
		prepare(setup) {
			const { DOM, data, api } = this;
			const { owner, theme } = setup;
			let { container } = setup;

			if (!this.isElement(container)) {
				if (this.isString(setup.name) && this.isElement(owner.container)) {
					container = DOM.get(`[name="${setup.name}"]`, owner.container);
				}
			}

			if (!this.isElement(container)) {
				container = document.createElement(`div`);
			}

			const dimensions = DOM.getDimensions(container);

			// Define read-only private properties
			this.defineProperties({
				/**
				 * The DOMElement that contains the current instance.
				 * @member {DOMElement} Controller#container
				 */
				container: { value: container },
				/**
				 * A shortcut to a DOM.getDimensions() call on the container.
				 * @member {Object} Controller#dimensions
				 */
				dimensions: { value: dimensions },
			});

			// Define read-only public properties
			Object.defineProperties(api, {
				container: { value: container },
				dimensions: { value: dimensions },
			});

			if (this.isString(theme, 1)) {
				data.theme = theme;
				Object.defineProperty(api, `theme`, { get: () => data.theme });
			}
		},
		constructor(setup) {
			const { DOM, Templates, data, namespace } = this;
			const { interceptEvents } = setup;
			const { theme } = data;
			const [elements, inputs, modules, widgets] = [{}, {}, {}, {}];
			const promises = [];
			let classes;

			DOM.register({ interceptEvents });

			/** Maps for storing different kinds of data storage for the instance */
			this.assign(this, { elements, inputs, modules, widgets });

			/** Add the appropriate classes, like 'Page Landing' or 'Input Textbox' */
			classes = namespace.replace(`s/`, ` `);
			classes += ` loading`;
			if (setup.hide) {
				classes += ` hidden`;

				if (this.isString(setup.hiddenClass)) {
					classes += ` ${setup.hiddenClass}`;
				}
			}

			this.addClass(classes);

			this.setTheme(theme);

			if (setup.loadTemplates) {
				promises.push(Templates.preload());
			}

			if (setup.loadStylesheet) {
				promises.push(DOM.loadStylesheet());
			}

			if (promises.length > 0) {
				return this.when({ promises });
			}
		},

		ready(setup) {
			const { DOM } = this;

			// deferred setup, will execute after inheriting class's constructors
			this.each(setup.listeners, function (listener) {
				// iterate over the listeners array
				this.defaults(listener, {
					type: `DOM`, // used in the switch statement below
					owner: this,
				});

				if (this.isInt(listener.throttle, 1)) {
					listener.handler = this.Utils.throttle(listener.handler, listener.throttle);
				}

				if (this.isInt(listener.debounce, 1)) {
					listener.handler = this.Utils.debounce(listener.handler, listener.debounce, listener.debounceImmediate);
				}

				switch (listener.type) {
					case `DOM`:
						this.defaults(listener, {
							filter: null, // detect events on the object directly, if no filter is passed
							subscribe: true, // whether to *always* fire the handler function (an example of false would be 'request the user once, no updates')
							trigger: false, // whether to execute the function immediately
						});

						return DOM.on(listener); // no break
				}
			});

			this.removeClass(`loading`);
		},

		api: {
			/**
			 * @method Controller#addClass
			 * @param {string} className
			 * @param {element} [target]
			 * @see DOM#addClass
			 */
			addClass: {
				value(className, target) {
					this.DOM.addClass(className, target);
				},
			},

			/**
			 * @method Controller#get
			 * @param {string} selector
			 * @param {element} [target]
			 * @return {element|null}
			 * @see DOM#get
			 */
			get: {
				value(selector, container) {
					return this.DOM.get(selector, container);
				},
			},

			/**
			 * @method Controller#hasClass
			 * @param {string} selector
			 * @param {element} [target]
			 * @return {boolean}
			 * @see DOM#hasClass
			 */
			hasClass: {
				value(className, element) {
					return this.DOM.hasClass(className, element);
				},
			},

			/**
			 * @method Controller#hide
			 * @param {Object} context
			 * @param {AsyncOptions} options
			 * @return {Promise}
			 * @see DOM#hide
			 */
			hide: {
				public: true,
				value(context, options) {
					return this.DOM.hide(options);
				},
			},

			/**
			 * Remove a DOM Element from the document.
			 * @method Controller#remove
			 * @param {Element} target
			 * @see DOM#remove
			 */
			remove: {
				value(element) {
					return this.DOM.remove(element);
				},
			},

			/**
			 * @method Controller#removeClass
			 * @param {string} className
			 * @param {element} [target]
			 * @see DOM#removeClass
			 */
			removeClass: {
				value(className, container) {
					return this.DOM.removeClass(className, container);
				},
			},

			/**
			 * @method Controller#select
			 * @param {string} selector
			 * @param {element} [target]
			 * @see DOM#select
			 */
			select: {
				value(selector, container) {
					return this.DOM.select(selector, container);
				},
			},

			/**
			 * Set the theme for this component
			 *
			 * Updates the container's theme attribute and stores in instance data.
			 * Theme is inherited by child components automatically.
			 *
			 * @method Controller#setTheme
			 * @param {Object} context - Calling instance (context argument pattern)
			 * @param {string} theme - Theme name (e.g., 'DARK', 'LIGHT')
			 * @returns {void}
			 *
			 * @example
			 * this.setTheme('DARK');
			 */
			setTheme: {
				public: true,
				value(_, theme) {
					const { data, container, api } = this;

					if (this.isString(theme, 1)) {
						container.setAttribute(`theme`, theme);
						data.theme = theme;
						api.theme = theme;
					}
				},
			},

			/**
			 * @method Controller#show
			 * @param {Object} context
			 * @param {AsyncOptions} options
			 * @return {Promise}
			 * @see DOM#show
			 */
			show: {
				public: true,
				value(context, options) {
					return this.DOM.show(options);
				},
			},
		},

		destructor: function (options) {
			options = this.defaults(options, {
				hide: true,
				transition: `none`,
				duration: 200, // only matters for non-none transitions
				remove: true,
			});

			// TODO: remove this; moved to Ancestor
			// if(this.isFunction(this.callbacks.destroy)) {
			//     this.callbacks.destroy(this);
			// }

			this.addClass(`hidden`); // add the hidden class reverse the transition

			if (options.hide) {
				return this.DOM.hide({
					target: this.container,
					transition: options.transition,
					duration: options.duration,
					remove: options.remove, // remove the container from the DOM upon completion
					then: function () {
						if (options.remove === false) {
							// the container is not being removed so do some clean up
							this.removeClass(this.namespace.replace(`s/`, ` `));
						}
					},
				});
			}
		},

		listeners: [
			{
				trigger: `visible`,
				handler() {
					this.removeClass(`hidden`);
				},
			},
		],
	});
});

//# sourceMappingURL=Controller.js.map